/**
 * ChronosBubble — co-op ability unlocked after Level 2 (Elder Gus).
 * Babylon.js port.
 *
 * Mechanic: Both cats activate co-op keys simultaneously.
 * Creates a localized time-dilation field where everything inside
 * moves at 30% normal speed — foxes, machinery, hostile forces.
 *
 * Visual: Expanding concentric torus rings in bioluminescent blue/purple,
 * plus a translucent sphere enclosing the affected area.
 *
 * Reduced-motion support:
 *   - particleDensityFactor() scales emitRate and capacity at activate() time.
 *   - largeVisualScale() shrinks the VISUAL sphere radius (maxRadius field and
 *     the bubbleSphere mesh scale). The GAMEPLAY radius (gameplayRadius field,
 *     returned by getBubbleData().radius) always eases toward maxRadiusBase
 *     and is completely unaffected by reducedMotion.
 *   - ACCESSIBILITY_CHANGED re-tunes emitRate live.
 *   - Particle capacity is fixed at ParticleSystem construction time by
 *     Babylon.js — only the NEXT activation will reflect a capacity change.
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Color4 } from "@babylonjs/core/Maths/math.color";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { ParticleSystem } from "@babylonjs/core/Particles/particleSystem";
import { Ability } from "../../systems/AbilitySystem";
import { gameEvents } from "../../core/EventBus";
import { GameEvents } from "../../config/events";
import { COLORS, OVERGROWN_CONSERVATORY } from "../../config/GameConfig";
import { AccessibilitySystem } from "../../systems/AccessibilitySystem";
import { particleDensityFactor, largeVisualScale } from "../../systems/particleScaling";
import { a11yColor3, a11yColor4 } from "../../systems/abilityColorHelpers";

export class ChronosBubble implements Ability {
  readonly id = "chronosBubble";
  readonly name = "Chronos Bubble";
  isActive = false;
  cooldownRemaining = 0;
  readonly cooldownDuration = 4; // seconds

  private scene: Scene;
  private basilMesh: AbstractMesh;
  private minnaMesh: AbstractMesh;

  private bubbleSphere: Mesh | null = null;
  private bubbleRings: Mesh[] = [];
  private particles: ParticleSystem | null = null;

  /** Visual radius — eases toward maxRadius (may be reduced by reducedMotion). */
  private bubbleRadius = 0;
  /** Gameplay radius — eases toward maxRadiusBase regardless of reducedMotion. */
  private gameplayRadius = 0;
  private readonly maxRadiusBase = OVERGROWN_CONSERVATORY.PUZZLE.CHRONOS_BUBBLE_RADIUS;
  /** Effective VISUAL maxRadius for the current activation (may be reduced by reducedMotion). */
  private maxRadius: number;
  private readonly timeScale = OVERGROWN_CONSERVATORY.PUZZLE.CHRONOS_BUBBLE_TIME_SCALE;
  private readonly bubbleDuration = 5; // seconds
  private bubbleTimer = 0;
  private readonly expandDuration = 0.6; // seconds
  private epicenter = Vector3.Zero();

  /** Base particle capacity/emitRate before reduced-motion scaling. */
  private static readonly BASE_CAPACITY = 60;
  private static readonly BASE_EMIT_RATE = 12;

  /** Unsubscribe function for ACCESSIBILITY_CHANGED listener. */
  private _a11yUnsub: (() => void) | null = null;

  constructor(scene: Scene, basilMesh: AbstractMesh, minnaMesh: AbstractMesh) {
    this.scene = scene;
    this.basilMesh = basilMesh;
    this.minnaMesh = minnaMesh;
    this.maxRadius = this.maxRadiusBase;

    // Live re-tune: update emitRate whenever reduced-motion preference changes.
    // NOTE: capacity is fixed at ParticleSystem construction time — only the
    // NEXT activation rescales capacity. emitRate can be updated live.
    this._a11yUnsub = gameEvents.on(GameEvents.ACCESSIBILITY_CHANGED, (settings) => {
      if (this.particles) {
        const factor = particleDensityFactor(settings.reducedMotion);
        this.particles.emitRate = ChronosBubble.BASE_EMIT_RATE * factor;
      }
    });
  }

  canActivate(): boolean {
    if (this.isActive || this.cooldownRemaining > 0) return false;
    const dist = Vector3.Distance(this.basilMesh.position, this.minnaMesh.position);
    return dist <= OVERGROWN_CONSERVATORY.PUZZLE.CHRONOS_BUBBLE_RANGE;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    this.isActive = true;
    this.bubbleRadius = 0;
    this.gameplayRadius = 0;
    this.bubbleTimer = 0;

    const reduced = AccessibilitySystem.getInstance().state.reducedMotion;
    const factor = particleDensityFactor(reduced);
    const visualScale = largeVisualScale(reduced);
    const rc = (k: string, f: string) => AccessibilitySystem.getInstance().resolveColor(k, f);

    // Reduced-motion: shrink the enclosing sphere's visual radius.
    // The gameplay radius in CHRONOS_BUBBLE_TRIGGERED still uses maxRadiusBase
    // so the time-dilation overlap check is not affected by accessibility mode.
    this.maxRadius = this.maxRadiusBase * visualScale;

    this.epicenter = Vector3.Center(this.basilMesh.position, this.minnaMesh.position);

    // --- Translucent enclosing sphere ---
    this.bubbleSphere = MeshBuilder.CreateSphere(
      "chronosBubble_sphere",
      { diameter: 0.1, segments: 24 },
      this.scene,
    );
    this.bubbleSphere.position = this.epicenter.clone();
    const sphereMat = new StandardMaterial("chronosBubble_sphereMat", this.scene);
    sphereMat.diffuseColor = a11yColor3('BIOLUMINESCENT_PURPLE', COLORS.BIOLUMINESCENT_PURPLE, rc);
    sphereMat.emissiveColor = a11yColor3('BIOLUMINESCENT_PURPLE', COLORS.BIOLUMINESCENT_PURPLE, rc).scale(0.15);
    sphereMat.alpha = 0.08;
    sphereMat.specularColor = Color3.Black();
    sphereMat.backFaceCulling = false;
    this.bubbleSphere.material = sphereMat;

    // --- Concentric torus rings ---
    const ringColors = [
      a11yColor3('BIOLUMINESCENT_BLUE', COLORS.BIOLUMINESCENT_BLUE, rc),
      a11yColor3('BIOLUMINESCENT_PURPLE', COLORS.BIOLUMINESCENT_PURPLE, rc),
      a11yColor3('BIOLUMINESCENT_PURPLE', '#4444ff', rc),
      a11yColor3('BIOLUMINESCENT_BLUE', COLORS.BIOLUMINESCENT_BLUE, rc),
    ];
    for (let i = 0; i < 4; i++) {
      const ring = MeshBuilder.CreateTorus(
        `chronosBubble_ring_${i}`,
        { diameter: 0.1, thickness: 0.03 - i * 0.005, tessellation: 32 },
        this.scene,
      );
      ring.position = this.epicenter.clone();
      ring.rotation.x = Math.PI / 2;

      const mat = new StandardMaterial(`chronosBubble_ringMat_${i}`, this.scene);
      mat.diffuseColor = ringColors[i];
      mat.emissiveColor = ringColors[i].scale(0.5);
      mat.alpha = 0.6;
      mat.specularColor = Color3.Black();
      ring.material = mat;

      this.bubbleRings.push(ring);
    }

    // --- Slow-motion particles — capacity and emitRate scaled by reducedMotion factor.
    // NOTE: capacity is fixed at construction time; only the NEXT activation
    // rescales capacity. emitRate is tunable live via ACCESSIBILITY_CHANGED.
    const capacity = Math.max(1, Math.ceil(ChronosBubble.BASE_CAPACITY * factor));
    this.particles = new ParticleSystem("chronosBubble_ps", capacity, this.scene);
    this.particles.emitter = this.epicenter.clone();
    this.particles.minLifeTime = 2;
    this.particles.maxLifeTime = 3;
    this.particles.minSize = 0.08;
    this.particles.maxSize = 0.2;
    this.particles.emitRate = ChronosBubble.BASE_EMIT_RATE * factor;
    this.particles.gravity = new Vector3(0, 0.5, 0);
    this.particles.direction1 = new Vector3(-3, -1, -3);
    this.particles.direction2 = new Vector3(3, 2, 3);
    this.particles.color1 = a11yColor4('BIOLUMINESCENT_BLUE', COLORS.BIOLUMINESCENT_BLUE, 0x99 / 255, rc);
    this.particles.color2 = a11yColor4('BIOLUMINESCENT_PURPLE', COLORS.BIOLUMINESCENT_PURPLE, 0x99 / 255, rc);
    this.particles.colorDead = new Color4(0, 0, 0, 0);
    this.particles.createSphereEmitter(this.maxRadiusBase * 0.4);
    this.particles.start();

    // Emit events — always use maxRadiusBase for gameplay overlap detection.
    gameEvents.emit(GameEvents.CHRONOS_BUBBLE_TRIGGERED, {
      x: this.epicenter.x,
      y: this.epicenter.y,
      z: this.epicenter.z,
      radius: this.maxRadiusBase,
      timeScale: this.timeScale,
    });

    gameEvents.emit(GameEvents.COOP_ABILITY_USED, { abilityId: this.id });

    return true;
  }

  update(dt: number): void {
    if (!this.isActive) return;

    this.bubbleTimer += dt;

    // Expand phase (back-ease-out approximation)
    const expandProgress = Math.min(this.bubbleTimer / this.expandDuration, 1);
    const eased = 1 - Math.pow(1 - expandProgress, 3); // ease-out cubic
    // Visual radius eases toward maxRadius (may be reduced by reducedMotion).
    this.bubbleRadius = this.maxRadius * eased;
    // Gameplay radius always eases toward maxRadiusBase — unaffected by reducedMotion.
    this.gameplayRadius = this.maxRadiusBase * eased;

    // Lifecycle fade
    const lifeProgress = this.bubbleTimer / this.bubbleDuration;
    const fadeAlpha = lifeProgress > 0.7 ? 1 - (lifeProgress - 0.7) / 0.3 : 1;

    // Update sphere scale
    if (this.bubbleSphere) {
      const scale = this.bubbleRadius * 2;
      this.bubbleSphere.scaling.setAll(Math.max(scale, 0.01));
      (this.bubbleSphere.material as StandardMaterial).alpha = 0.08 * fadeAlpha;
    }

    // Update ring scales with pulsing
    const now = this.bubbleTimer * 1000;
    for (let i = 0; i < this.bubbleRings.length; i++) {
      const ring = this.bubbleRings[i];
      const ringRadius = this.bubbleRadius * (1 - i * 0.12);
      if (ringRadius > 0) {
        const pulse = Math.sin(now * 0.003 + i * 0.8) * 0.15 + 0.85;
        const ringScale = ringRadius * 2 * pulse;
        ring.scaling.setAll(Math.max(ringScale, 0.01));

        const mat = ring.material as StandardMaterial;
        mat.alpha = fadeAlpha * pulse * (1 - i * 0.2) * 0.6;
      }
    }

    // End bubble
    if (this.bubbleTimer >= this.bubbleDuration) {
      this.deactivate();
    }
  }

  deactivate(): void {
    this.isActive = false;
    this.cooldownRemaining = this.cooldownDuration;

    if (this.bubbleSphere) {
      this.bubbleSphere.dispose();
      this.bubbleSphere = null;
    }
    for (const ring of this.bubbleRings) {
      ring.dispose();
    }
    this.bubbleRings = [];
    if (this.particles) {
      this.particles.stop();
      this.particles.dispose();
      this.particles = null;
    }

    gameEvents.emit(GameEvents.CHRONOS_BUBBLE_ENDED);
  }

  dispose(): void {
    this.deactivate();
    if (this._a11yUnsub) {
      this._a11yUnsub();
      this._a11yUnsub = null;
    }
  }

  /** Get the current bubble data for overlap checking by the scene.
   *  radius is the GAMEPLAY radius (always eases toward maxRadiusBase,
   *  unaffected by reducedMotion visual scaling). */
  getBubbleData(): { x: number; y: number; z: number; radius: number; timeScale: number } | null {
    if (!this.isActive) return null;
    return {
      x: this.epicenter.x,
      y: this.epicenter.y,
      z: this.epicenter.z,
      radius: this.gameplayRadius,
      timeScale: this.timeScale,
    };
  }
}
